import { useQuery } from '@tanstack/react-query'
import useManagerStore from '../store/managerStore'
import { getTenant } from '../api/tenant'
import { normalizeTenantId } from '../lib/tenantId'
import { DEFAULT_CURRENCY, SUPPORTED_CURRENCIES } from '../lib/money'

// Shared query keys for the tenant record (GET /admin/tenants/{id}), so later
// callers (settings card, polling) reuse one cache entry per tenant.
export const tenantKeys = {
  all: ['tenant'],
  detail: (tenantId) => ['tenant', 'detail', tenantId],
}

function toSupportedCurrency(value) {
  if (typeof value !== 'string') return null
  const code = value.trim().toUpperCase()
  return SUPPORTED_CURRENCIES.includes(code) ? code : null
}

/**
 * Effective tenant currency (SPEC-growchat-tenant-currency.md, Design 8/9):
 * a pending `currency_change.to` wins over `currency`. Unknown or missing
 * values fall back to VND (backend default).
 */
export function resolveTenantCurrency(tenant) {
  return (
    toSupportedCurrency(tenant?.currency_change?.to) ??
    toSupportedCurrency(tenant?.currency) ??
    DEFAULT_CURRENCY
  )
}

/** Shared query definition for the tenant record; reuse it for every read. */
export function tenantQueryOptions(tenantId) {
  return {
    queryKey: tenantKeys.detail(tenantId),
    queryFn: () => getTenant(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  }
}

/**
 * Effective currency of the active tenant plus resolution state.
 * `currency` is VND until the tenant is resolved (tenant data present; not
 * while loading, on a first-load error, or with no tenant). Money inputs (C31) must gate on `isResolved` instead of trusting
 * the VND placeholder.
 */
export function useTenantCurrencyState() {
  const tenantId = normalizeTenantId(useManagerStore((s) => s.tenantId))
  const { data, isLoading, isError } = useQuery(tenantQueryOptions(tenantId))
  return {
    currency: resolveTenantCurrency(data),
    isLoading,
    isError,
    // Resolved as soon as tenant data exists: a failed background refetch keeps
    // the cached tenant, so inputs must not be disabled mid-edit.
    isResolved: data !== undefined,
  }
}

/** Effective currency string; VND while unresolved (see useTenantCurrencyState). */
export default function useTenantCurrency() {
  return useTenantCurrencyState().currency
}
